import type { TemplateRef, Type } from '@angular/core';
import { Observable } from 'rxjs';
import { Color, Quaternion, Sprite, Vector3 } from 'three';
import {
  BlueprintComponent,
  PlaneConfiguration,
} from '../base-objects/blueprint.base';
import { MeshComponent, MeshConfiguration } from '../base-objects/mesh.base';
import {
  SpriteComponent,
  SpriteConfiguration,
} from '../base-objects/sprite.base';
import { TextComponent, TextConfiguration } from '../base-objects/text.base';
import { ThreeObjectBase } from '../base-objects/three-object.base';

export interface ICustomComponent<T> {
  /**
   * @description  (string | undefined): The ID of the currently selected tag component. Set by the host
   * (e.g. the element open on a side sheet), never by clicks; the component renders its `selectedHalo`
   * while selected. Selecting an id whose component does not exist (yet) is allowed: the state is applied
   * when the component is added.
   */
  set selectedNote(noteId: string | undefined);
  get selectedNote(): string | undefined;
  /**
   * @description  (Observable): An observable that emits the ID of the selected tag component when it changes.
   */
  readonly selectedNote$: Observable<string | undefined>;
  /**
   * @description  (Observable): An observable that emits click event emitters when a tag component is clicked.
   */
  readonly componentClicked$: Observable<ClickEventEmitter>;
  /**
   * @description  (Observable): Emits the currently hovered tag component (with its tooltip configuration)
   * or `undefined` when the pointer leaves it. At most one component is hovered at a time.
   */
  readonly componentHovered$: Observable<HoverEventEmitter | undefined>;
  /**
   * @description (function): Builds the same payload `componentHovered$` emits for the component with the given id
   * (anchor point, radius, tooltip), or `undefined` when it does not exist. Lets the host show the tooltip without a
   * real hover, e.g. on the first tap on a touch device.
   */
  getHoverEvent: (id: string) => HoverEventEmitter | undefined;
  /**
   * @param { ComponentConfiguration} component : {@link ComponentConfiguration}.
   * @description (function): Adds a tag component to the scene and returns an observable that emits the added component.
   */
  addComponent$: (component: ComponentConfiguration) => Observable<T>;
  /**
   * @param { ComponentConfiguration} component : {@link ComponentConfiguration}.
   * @description (function): Adds a tag component to the scene with a position offset calculated using the offset object, and returns an observable that emits the added component.
   */
  addComponentWithOffset$: (
    component: ComponentConfiguration,
    isBlueprint?: boolean
  ) => Observable<T>;
  /**
   * @param { ComponentConfiguration} component : {@link ComponentConfiguration}.
   * @description   (function): Updates the position, normal, and stem height of a tag component and returns an observable that emits when the update is complete.
   */
  updateNote$: (
    component: Pick<
      ComponentConfiguration,
      'id' | 'position' | 'normal' | 'stemHeight'
    >
  ) => Observable<void>;
  /**
   *
   * @description   (function): Updates the position, normal, and stem height of a tag component .
   */
  updateNote: (
    component: Pick<
      ComponentConfiguration,
      'id' | 'position' | 'normal' | 'stemHeight'
    >
  ) => void;
  /**
   * @description   (function): Updates the position, normal, stem height, rotation, and scale of a tag component with a position offset calculated using the offset object.
   */
  updatePositionWithOffset: (
    component: Pick<
      ComponentConfiguration,
      'id' | 'position' | 'normal' | 'stemHeight' | 'rotation' | 'scale'
    >
  ) => void;
  /**
   * @description  (function): Same as updatePositionWithOffset, but returns an observable.
   */
  updatePositionWithOffset$: (
    component: Pick<
      ComponentConfiguration,
      'id' | 'position' | 'normal' | 'stemHeight' | 'rotation' | 'scale'
    >
  ) => Observable<void>;
  /**
   * @description  (function): Deletes a tag component from the scene.
   */
  deleteNote: (noteId: string) => string;
  /**
   * @description  (function): Same as deleteNote, but returns an observable.
   */
  deleteNote$: (noteId: string) => Observable<string>;
  /**
   * @description   (function): Deletes all tag components from the scene.
   */
  clearAllNotes: () => void;
  /**
   * @description (function): Same as clearAllNotes, but returns an observable.
   */
  clearAllNotes$: () => Observable<void>;
}

export type Inputs = Transformation & {
  id: string;
  visible?: boolean;
  renderOrder?: number;
  opacity?: number;
  scale: Vector3;
  isCollider?: boolean;
  showhelperBox?: boolean;
  autoScale?: boolean;
  userData?:
    | (Record<string, unknown> & { type?: UComponentType | string,  annotationType?: AnnotationType; })
    | undefined
    | null;
  depthTest?: boolean;
  up?: Vector3;
  transparent?: boolean;
  lookAt?: boolean;
  dollhouseView?: boolean;
  /**
   * Optional tooltip rendered (in the DOM, by `TagTooltipHostComponent`) while the component is hovered.
   */
  tooltip?: TooltipConfiguration | null;
  objects: ChildConfiguration[];
};

/**
 * Context handed to a tooltip rendered from a `TemplateRef`.
 * `$implicit` is the component `userData`, so `*ngTemplateOutlet="tpl; context: ctx"` gives `let-data`.
 */
export type TooltipTemplateContext<
  TData = Inputs['userData'],
  TExtra extends Record<string, unknown> = Record<string, unknown>
> = {
  $implicit: TData;
  id: string;
  userData: TData;
} & TExtra;

/**
 * Tooltip content is either an Angular template (rendered with `ngTemplateOutlet`)
 * or an Angular component (rendered with `ngComponentOutlet`). In both cases the
 * component `id` and `userData` are provided to the content.
 */
export type TooltipConfiguration =
  | {
      template: TemplateRef<TooltipTemplateContext<any, any>>;
      /** Extra values merged into the template context. */
      context?: Record<string, unknown>;
    }
  | {
      component: Type<unknown>;
      /** Inputs set on the created component (in addition to `id` and `userData` when declared). */
      inputs?: Record<string, unknown>;
    };

export function isTemplateTooltip(
  tooltip: TooltipConfiguration
): tooltip is Extract<TooltipConfiguration, { template: unknown }> {
  return 'template' in tooltip && !!tooltip.template;
}
export const ComponentTypes = ['portal', 'blueprint'] as const;
export type UComponentType = typeof ComponentTypes[number];
export type Transformation = {
  position: Vector3;
  rotation?: Quaternion;
  normal: Vector3;
  stemHeight: number;
};

export interface ComponentTextDefinition {
  text: string;
  value?: unknown;
  weight?: number;
  propertyType?: string;
  backgroundColor?: Color;
}

export type ComponentsTypeClass =
  | SpriteComponent
  | TextComponent
  | MeshComponent
  | BlueprintComponent;

export type ClickEventEmitter = {
  id: string;
  userData: Record<string, unknown> | undefined | null;
};

export type HoverEventEmitter = ClickEventEmitter & {
  /** World position of the hovered component (tooltip anchor). */
  point: Vector3;
  /** Bounding-sphere radius of the component in world units (lets the tooltip stay clear of the marker). */
  radius?: number;
  tooltip: TooltipConfiguration | null | undefined;
};

/** Dispatched on a collidable group by `SimlabCollider.enableHover()` (see `HOVER_ENTER_EVENT` in common). */
export type HoverEnterEvent = { type: 'onHoverEnter'; point: Vector3 };
export type HoverLeaveEvent = { type: 'onHoverLeave' };

export type NoteComponentClickData = {
  collider: Sprite;
  input: ClickEvent;
  normal: null;
  point: Vector3;
  hover?: boolean;
  userData?: unknown;
};
export type ClickEvent = {
  button: number;
  eventType: string;
  position: Vector3;
};

export type ChildConfiguration =
  | TextConfiguration
  | SpriteConfiguration
  | PlaneConfiguration
  | MeshConfiguration;

export type ComponentConfiguration = Inputs & {
  objects: ChildConfiguration[];
};

// export type ThreeObject<T = ThreeObjectBase> = T extends { icon: string }
//   ? SpriteConfiguration
//   : TextConfiguration;

export function generateClassInstance<
  T extends ThreeObjectBase,
  W extends ObjectConfig<T>
>(type: IConstructor<T>, three: typeof import('three'), configuration: W): T {
  return new type(three, configuration.config);
}
export type IAbstractConstructor<T = object> = abstract new (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ...args: any[]
) => T;
export interface IConstructor<T> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  new (...args: any[]): T;
}
export type ObjectConfig<T> = {
  config: Partial<Omit<Inputs, 'id'>>;
  get instance(): T;
  init(three: typeof import('three')): void;
};
export type PropertiesOf<T> = { [K in keyof T]: T[K] };
export type AnnotationType = 'note' | 'punch-item' | 'rfi';